import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import ChainedBackend from 'i18next-chained-backend'
import { GatedHttpBackend } from './gatedHttpBackend'
import LocalStorageBackend from 'i18next-localstorage-backend'
import resourcesToBackend from 'i18next-resources-to-backend'
import { enStandard } from './resources/enStandard'
import { NAMESPACES } from './termKeys'
import { httpBackendOptions } from './httpBackend'
import { resetServerLocales, setServerLocales } from './serverLocales'
import {
  localeGraphChanged,
  readCachedProfiles,
  writeCachedProfiles,
} from './profileCache'
import { terminologyApi, type TermProfile } from '@/api/terminologyApi'

export const BASE_LOCALE = 'en-standard'
export const DEFAULT_NS = 'cgt' // generic namespace: t('key') with no ns resolves here
/** Persisted active-locale key (mirrors the TenantProvider localStorage pattern). */
export const LOCALE_STORAGE_KEY = 'cgt_terminology_locale'
/** localStorage prefix of the namespace cache (`<prefix><locale>-<namespace>`). */
const TERMS_CACHE_PREFIX = 'cgt_terms_'

function browserStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    // Storage-restricted contexts (private mode, blocked cookies) throw SecurityError.
    return null
  }
}

// ─── Dynamic fallback model ──────────────────────────────────────────────────
// Locales are created/removed at runtime, so the inheritance graph is learned from
// the backend rather than hardcoded. `localeFallbacks` maps each composite code to
// its immediate fallback; `fallbackLng` (a function) resolves the full chain.
const localeFallbacks = new Map<string, string | null>([
  [BASE_LOCALE, null],
  ['en-clinical', BASE_LOCALE], // sensible default before /profiles loads
])

/** The list the current graph was built from; `null` until one is applied. */
let graphProfiles: TermProfile[] | null = null

/**
 * (Re)build the fallback graph from a profile list the server just returned
 * (call after add/remove), and keep it for the next page load to boot from.
 * Returns whether the graph changed from the one in effect.
 *
 * On a change, the namespace cache of every locale involved is dropped. The
 * server merges a locale's terms down its fallback chain, so a re-parent makes
 * a cached merge wrong, and a locale the old list lacked was gated off HTTP
 * (serverLocales.ts), so what is cached under its code is the bundled base.
 * The LocalStorage link is read before HTTP, `reloadResources` included, so
 * either would otherwise be served back for the cache's 24h lifetime. Graph
 * changes are rare; the cost is one refetch of the namespaces.
 */
export function applyLocales(profiles: TermProfile[]): boolean {
  const before = graphProfiles
  const changed = before !== null && localeGraphChanged(before, profiles)
  setLocaleGraph(profiles)
  const storage = browserStorage()
  writeCachedProfiles(storage, profiles)
  if (changed) {
    const locales = new Set([BASE_LOCALE, ...before.map((p) => p.id), ...profiles.map((p) => p.id)])
    for (const locale of locales) {
      for (const ns of NAMESPACES) {
        try {
          storage?.removeItem(`${TERMS_CACHE_PREFIX}${locale}-${ns}`)
        } catch {
          // blocked storage: nothing was cached either
        }
      }
    }
  }
  return changed
}

/** Build the fallback graph without persisting it (booting from the cached copy). */
function setLocaleGraph(profiles: TermProfile[]): void {
  graphProfiles = profiles
  localeFallbacks.clear()
  localeFallbacks.set(BASE_LOCALE, null)
  for (const p of profiles) localeFallbacks.set(p.id, p.fallback)
  // BASE_LOCALE is seeded above whether or not the backend has it, because the
  // fallback chain must always end somewhere. What the backend actually holds is
  // a different question, and the terms fetcher needs that one answered
  // honestly — see serverLocales.ts.
  setServerLocales(profiles.map(p => p.id))
}

/** Full fallback chain for a locale (excludes itself), always ending at the base. */
function resolveChain(code: string): string[] {
  const chain: string[] = []
  const seen = new Set<string>([code])
  let cursor = localeFallbacks.get(code) ?? null
  while (cursor && !seen.has(cursor)) {
    chain.push(cursor)
    seen.add(cursor)
    cursor = localeFallbacks.get(cursor) ?? null
  }
  if (!chain.includes(BASE_LOCALE)) chain.push(BASE_LOCALE)
  return chain
}

function readStoredLocale(): string | null {
  try {
    return browserStorage()?.getItem(LOCALE_STORAGE_KEY) ?? null
  } catch {
    return null
  }
}

const storedLocale = readStoredLocale()

// Registered once, here, so a retried init does not stack them.
i18n.use(ChainedBackend).use(initReactI18next)

// Recorded from import time, not from I18nProvider's effect: init now starts
// before the first render (main.tsx), so a namespace load can fail before the
// provider has subscribed, and the offline banner must still show.
let termsLoadFailed = false
i18n.on('failedLoading', () => {
  termsLoadFailed = true
})

/** Has any namespace failed to load from the backend since boot? */
export function didTermsFailToLoad(): boolean {
  return termsLoadFailed
}

let initPromise: Promise<typeof i18n> | null = null

/**
 * Initialize i18next once.
 *
 * The locale list decides the fallback graph and which locales the server can
 * answer, so `init` needs it first. Where it comes from:
 *
 * - **A list cached by an earlier load** (any visit after the first): apply it
 *   and init straight away, then fetch the current list in the background and
 *   apply it if the graph changed. With the namespaces also cached, `init`
 *   completes synchronously (`initAsync: false`), so when this is called before
 *   the first render (`main.tsx`) the app never shows "Loading terminology…"
 *   and page requests start without waiting for `/terms/profiles` (z8nrz7fxhz).
 * - **No cached list** (first visit): wait for the server, as before. On
 *   failure (offline) init with the bundled defaults, so the app still renders
 *   `en-standard`.
 */
export function initI18n(): Promise<typeof i18n> {
  if (i18n.isInitialized) return Promise.resolve(i18n)
  if (initPromise) return initPromise

  const cached = readCachedProfiles(browserStorage())
  const promise = cached ? bootFromCache(cached) : bootFromServer()

  // Clear the cached promise on failure so the next initI18n() call can retry
  // instead of returning the same rejection forever.
  initPromise = promise.catch((err) => {
    initPromise = null
    throw err
  })

  return initPromise
}

async function bootFromServer(): Promise<typeof i18n> {
  try {
    applyLocales(await terminologyApi.listProfiles())
  } catch {
    // keep the bundled defaults already in localeFallbacks
  }
  return startInit()
}

function bootFromCache(cached: TermProfile[]): Promise<typeof i18n> {
  setLocaleGraph(cached)
  // Fetch the current list in parallel with init, not after it.
  const fresh = terminologyApi.listProfiles()
  // Synchronous up to the first backend read, deliberately: see initI18n.
  const ready = startInit()
  void refreshLocales(fresh, ready).catch((err) => {
    console.warn('[i18n] could not apply the refreshed locale list', err)
  })
  return ready
}

/**
 * Apply the server's current list once init is done, and re-render if it
 * changed the graph (a locale added, removed or re-parented since the cached
 * copy was written, e.g. by another admin or in another browser).
 */
async function refreshLocales(
  fresh: Promise<TermProfile[]>,
  ready: Promise<unknown>,
): Promise<void> {
  let profiles: TermProfile[]
  try {
    profiles = await fresh
  } catch {
    // Unreachable or refused: the cached list is not an answer from the server
    // this session, so it must not keep the HTTP link closed (serverLocales.ts).
    // Back to "unknown", which is what a failed list meant before this cache.
    resetServerLocales()
    return
  }
  await ready
  // Also rewrites the stored copy, so labels stay current even when unchanged.
  if (!applyLocales(profiles)) return

  await i18n.reloadResources([i18n.language, ...resolveChain(i18n.language)])
  // react-i18next re-renders on `languageChanged`, not on a resource reload.
  // Read the language again: the person may have switched during the reload.
  await i18n.changeLanguage(i18n.language)
}

function startInit(): Promise<typeof i18n> {
  return i18n
    .init({
      lng: storedLocale ?? BASE_LOCALE,
      // Function form: resolve the chain dynamically so new locales inherit correctly.
      fallbackLng: (code?: string) => (code ? resolveChain(code) : [BASE_LOCALE]),
      supportedLngs: false, // allow any composite code → new langs are switchable
      load: 'currentOnly', // don't split composite codes into base languages
      ns: NAMESPACES,
      defaultNS: DEFAULT_NS,
      // Load in this call rather than on the next tick. When every namespace is
      // in the localStorage cache, init then finishes before it returns, which is
      // what lets the first render skip the loading screen. A cache miss still
      // goes to the network and resolves later, exactly as before.
      initAsync: false,
      // Admins can add arbitrary term keys; a '.' must be treated as a literal
      // character, not a nested-key separator, so lookups never break.
      keySeparator: false,
      interpolation: { escapeValue: false }, // React already escapes
      returnNull: false,
      backend: {
        backends: [
          LocalStorageBackend, // 1. cache (busted by reloadResources after an edit)
          GatedHttpBackend, // 2. backend API (merged base ⊕ override), when it has the locale
          resourcesToBackend({ [BASE_LOCALE]: enStandard }), // 3. bundled offline fallback
        ],
        backendOptions: [
          {
            prefix: TERMS_CACHE_PREFIX,
            expirationTime: 24 * 60 * 60 * 1000, // 24h; edits bust via reloadResources
            defaultVersion: 'v1',
          },
          httpBackendOptions,
          {}, // resourcesToBackend takes no options
        ],
      },
      // Logs missing terms instead of silently rendering the key. Providing this
      // handler also REPLACES i18next's saveMissing backend write (no stray POSTs).
      saveMissing: true,
      missingKeyHandler: (lngs, ns, key) => {
        console.warn(`[i18n] missing term: ${ns}:${key} (locale ${lngs.join(',')})`)
      },
    })
    .then(() => i18n)
}

export default i18n
